import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../styles/Bookings.css";

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-Au", {
      timeZone: "Australia/Brisbane",
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(date));
  };

  const formatBookingDate = (date) => {
    return new Intl.DateTimeFormat("en-Au", {
      timeZone: "Australia/Brisbane",
      day: "2-digit",
      month: "short",
      year: "2-digit",
    }).format(new Date(date));
  };

  const formatPhone = (phone) => {
    if (!phone) return "";

    let cleaned = phone.replace(/\D/g, "");

    if (cleaned.startsWith("61")) {
      cleaned = "0" + cleaned.slice(2);
    } else if (cleaned.startsWith("+61")) {
      cleaned = "0" + cleaned.slice(3);
    }

    if (cleaned.length === 10 && cleaned.startsWith("04")) {
      return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, "$1 $2 $3");
    }

    if (/^0[2-8]\d{8}$/.test(cleaned)) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3");
    }

    // Fallback: NOT starting with 0 → 4 + 3 grouping
    if (!cleaned.startsWith("0")) {
      const first = cleaned.slice(0, 4);
      const rest = cleaned.slice(4);

      const groupedRest = rest.match(/\d{1,4}/g)?.join(" ") || "";

      return [first, groupedRest].filter(Boolean).join(" ");
    }

    return cleaned;
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch bookings data from the backend API
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/bookings`,
          {
            credentials: "include",
          },
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch bookings");
        }

        setBookings(data.bookings);
      } catch (err) {
        toast.error(err.message || "Failed to fetch bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  if (loading) {
    return <div>Loading bookings...</div>;
  }

  return (
    <div className="bookings__container">
      <h2>Bookings</h2>
      {bookings.length === 0 ? (
        <h2>No bookings found.</h2>
      ) : (
        <div className="bookings__table--wrapper">
          <table className="bookings__table">
            <thead className="bookings__table--header">
              <tr>
                <th data-label="Invoice Number">Invoice Number</th>
                <th data-labal="Booking Date">Booking Date</th>
                <th data-label="Name">Name</th>
                <th data-label="Email">Email</th>
                <th data-label="Phone">Phone</th>
                <th data-label="Picnic Date">Picnic Date</th>
                <th data-label="Preferred Location">Preferred Location</th>
                <th data-label="Picnic Type">Picnic Setting</th>
                <th data-label="Picnic Style">Picnic Style</th>
                <th data-label="Celebration Type">Celebration Type</th>
                <th data-label="Guest No">Guest No</th>
                <th data-label="Event Inclusions">Event Inclusions</th>
                <th data-label="Inclusions Cost">Inclusion Cost</th>
                <th data-label="Additions">Additions</th>
                <th data-label="Additions Cost">Additions Cost</th>
                <th data-label="Total Cost">Total</th>
                <th data-label="Paid Cost">Paid</th>
                <th data-label="Balance Cost">Balance</th>
                <th data-label="Additional Notes">Additional Notes</th>
              </tr>
            </thead>
            <tbody className="bookings__table--body">
              {[...bookings]
                .sort((a, b) => new Date(b.picnicDate) - new Date(a.picnicDate))
                .map((booking) => (
                  <tr
                    key={booking._id}
                    onClick={() =>
                      navigate(`/dashboard/bookings/${booking._id}`)
                    }
                    className="booking__table--row"
                  >
                    <td data-label="Invoice Number">{booking.invoiceNumber}</td>
                    <td data-label="Booking Date">
                      {" "}
                      {formatBookingDate(booking.createdAt)}
                    </td>
                    <td data-label="Name">{booking.name}</td>
                    <td data-label="Email">{booking.email}</td>
                    <td data-label="Phone">{formatPhone(booking.phone)}</td>
                    <td data-label="Picnic Date">
                      {formatDate(booking.picnicDate)}
                    </td>
                    <td data-label="Preferred Location">
                      {booking.preferredLocation}
                    </td>
                    <td data-label="Picnic Setting">{booking.picnicSetting}</td>
                    <td data-label="Picnic Style">{booking.picnicStyle}</td>
                    <td data-label="Celebration Type">
                      {booking.celebrationType}
                    </td>
                    <td data-label="Guest No">{booking.guestNo}</td>
                    <td data-label="Event Inclusions">
                      <ul>
                        {booking.eventInclusions
                          ?.flatMap((note) => note.split("\n"))
                          ?.map((item, i) => (
                            <li key={i}>{item.trim()}</li>
                          ))}
                      </ul>
                    </td>
                    <td data-label="Inclusions Cost">
                      ${booking.inclusionsCost}
                    </td>
                    <td data-label="Additions">
                      <ul>
                        {booking.additions?.map((item, i) => (
                          <li key={i}>
                            {item.name}: {item.quantity} @${item.price}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td data-label="Additions Cost">
                      ${booking.additionsCost}
                    </td>
                    <td data-label="Total Cost">${booking.totalCost}</td>
                    <td data-label="Paid Cost">${booking.paidCost}</td>
                    <td data-label="Balance Cost">${booking.balanceCost}</td>
                    <td data-label="Additional Notes">
                      <ul>
                        {booking.additionalNotes
                          ?.flatMap((note) => note.split("\n"))
                          ?.map((item, i) => (
                            <li key={i}>{item.trim()}</li>
                          ))}
                      </ul>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
